import React, { useState, useEffect, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../utils/cropImage';
import { api } from '../services/api';

// Inline CSS for animations
const styleSheet = document.createElement("style");
styleSheet.innerText = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;
document.head.appendChild(styleSheet);

export default function ItemFormModal({ isOpen, onClose, onSubmit, initialData = null, categories, currentCategoryId, isDarkMode }) {
    const [formData, setFormData] = useState({
        name: '',
        price: '',
        offerPrice: '',
        stockQuantity: 0,
        trackStock: false,
        isAvailable: true,
        image: null,
        description: '',
        categoryId: '',
        variants: []
    });
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Crop State
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [isCropping, setIsCropping] = useState(false);
    const [originalImage, setOriginalImage] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setIsSubmitting(false);
            if (initialData) {
                setFormData({
                    name: initialData.name || '',
                    price: initialData.price || '',
                    offerPrice: initialData.offerPrice || '',
                    stockQuantity: initialData.stockQuantity || 0,
                    trackStock: initialData.trackStock || false,
                    isAvailable: initialData.isAvailable !== undefined ? initialData.isAvailable : true,
                    estimatedPrepTime: initialData.estimatedPrepTime || 15,
                    image: null,
                    description: initialData.description || '',
                    categoryId: initialData.categoryId || currentCategoryId || '',
                    variants: initialData.variants || []
                });
                setPreviewUrl(initialData.imageUrl || null);
            } else {
                setFormData({
                    name: '',
                    price: '',
                    offerPrice: '',
                    stockQuantity: 0,
                    trackStock: false,
                    isAvailable: true,
                    estimatedPrepTime: 15,
                    image: null,
                    description: '',
                    categoryId: currentCategoryId || '',
                    variants: []
                });
                setPreviewUrl(null);
            }
        }
    }, [isOpen, initialData]);

    // Crop & Delete Handlers
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const compressImage = (blob) => {
        return new Promise((resolve, reject) => {
            const maxFileSize = 1024 * 1024; // 1MB
            if (blob.size <= maxFileSize) {
                resolve(blob);
                return;
            }
            const img = new Image();
            img.src = URL.createObjectURL(blob);
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0);
                let quality = 0.9;
                const compress = () => {
                    canvas.toBlob(
                        (newBlob) => {
                            if (newBlob.size > maxFileSize && quality > 0.1) {
                                quality -= 0.1;
                                compress();
                            } else {
                                resolve(newBlob);
                            }
                        },
                        'image/jpeg',
                        quality
                    );
                };
                compress();
            };
            img.onerror = reject;
        });
    };

    const showCropper = (file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            setOriginalImage(reader.result);
            setIsCropping(true);
            setZoom(1);
            setCrop({ x: 0, y: 0 });
        };
    };

    const handleCropSave = async () => {
        try {
            const croppedBlob = await getCroppedImg(originalImage, croppedAreaPixels);
            const compressedBlob = await compressImage(croppedBlob);
            const processedFile = new File([compressedBlob], 'food-item.jpg', { type: 'image/jpeg' });
            setFormData({ ...formData, image: processedFile });
            setPreviewUrl(URL.createObjectURL(processedFile));
            setIsCropping(false);
            setOriginalImage(null);
        } catch (e) {
            console.error(e);
            alert('Failed to crop image');
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            showCropper(file);
            e.target.value = null;
        }
    };

    const handleDelete = async () => {
        if (!initialData || !initialData.id) return;
        if (!window.confirm("Are you sure you want to delete this item? This cannot be undone.")) return;

        setIsSubmitting(true);
        try {
            // Use the centralized API instance
            await api.delete(`/restaurant/item/${initialData.id}`);

            setIsSubmitting(false);
            onClose();
            window.location.reload();
        } catch (error) {
            console.error("Delete error:", error);
            alert("Failed to delete item.");
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await onSubmit(formData);
        } catch (error) {
            console.error("Submit error:", error);
            alert(error.response?.data?.error || "Failed to save item. Please try again.");
            setIsSubmitting(false);
        }
    };

    const styles = getStyles(isDarkMode);

    if (!isOpen) return null;

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                {isSubmitting && (
                    <div style={styles.loadingOverlay}>
                        <div style={styles.spinner}></div>
                        <div style={{ marginTop: '15px', fontWeight: '500' }}>Uploading Item...</div>
                    </div>
                )}
                <div style={styles.header}>
                    <h3>{initialData ? 'Edit Item' : 'Add New Item'}</h3>
                    <button onClick={onClose} style={styles.closeBtn} disabled={isSubmitting}>&times;</button>
                </div>

                <form onSubmit={handleSubmit} style={styles.form}>
                    {isCropping ? (
                        <div style={styles.cropperContainer}>
                            <div style={styles.cropperWrapper}>
                                <Cropper
                                    image={originalImage}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={1}
                                    onCropChange={setCrop}
                                    onCropComplete={onCropComplete}
                                    onZoomChange={setZoom}
                                />
                            </div>
                            <div style={styles.controls}>
                                <div style={styles.controlRow}>
                                    <label style={styles.label}>Zoom</label>
                                    <input
                                        type="range"
                                        value={zoom}
                                        min={1}
                                        max={3}
                                        step={0.1}
                                        aria-labelledby="Zoom"
                                        onChange={(e) => setZoom(e.target.value)}
                                        className="zoom-range"
                                        style={styles.zoomRange}
                                    />
                                </div>
                                <div style={styles.cropActions}>
                                    <button
                                        type="button"
                                        onClick={() => { setIsCropping(false); setOriginalImage(null); }}
                                        style={styles.cancelBtn}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCropSave}
                                        style={styles.saveBtn}
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div style={styles.imageSection}>
                            <div style={styles.previewContainer}>
                                {previewUrl ? (
                                    <img src={previewUrl} alt="Preview" style={styles.imagePreview} />
                                ) : (
                                    <div style={styles.placeholderImage}>No Image</div>
                                )}
                            </div>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                style={{ marginTop: '10px' }}
                            />
                        </div>
                    )}

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Category</label>
                        <select
                            value={formData.categoryId}
                            onChange={e => setFormData({ ...formData, categoryId: e.target.value })}
                            style={styles.input}
                        >
                            <option value="">Select Category</option>
                            {categories && categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Item Name</label>
                        <input
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            required
                            style={styles.input}
                            placeholder="e.g. Butter Chicken"
                        />
                    </div>

                    <div style={styles.row}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Price (₹)</label>
                            <input
                                type="number"
                                value={formData.price}
                                onChange={e => setFormData({ ...formData, price: e.target.value })}
                                required
                                style={styles.input}
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Offer Price (₹)</label>
                            <input
                                type="number"
                                value={formData.offerPrice}
                                onChange={e => setFormData({ ...formData, offerPrice: e.target.value })}
                                style={styles.input}
                                placeholder="Optional"
                            />
                        </div>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Description</label>
                        <textarea
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            style={{ ...styles.input, minHeight: '60px' }}
                            placeholder="Short description..."
                        />
                    </div>

                    <div style={styles.row}>
                        <label style={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={formData.trackStock}
                                onChange={e => setFormData({ ...formData, trackStock: e.target.checked })}
                            />
                            Track Stock
                        </label>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Prep Time (min)</label>
                            <input
                                type="number"
                                value={formData.estimatedPrepTime}
                                onChange={e => setFormData({ ...formData, estimatedPrepTime: e.target.value })}
                                style={{ ...styles.input, width: '80px' }}
                            />
                        </div>

                        {formData.trackStock && (
                            <div style={styles.formGroup}>
                                <input
                                    type="number"
                                    placeholder="Qty"
                                    value={formData.stockQuantity}
                                    onChange={e => setFormData({ ...formData, stockQuantity: e.target.value })}
                                    style={{ ...styles.input, width: '80px' }}
                                />
                            </div>
                        )}

                        <label style={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={formData.isAvailable}
                                onChange={e => setFormData({ ...formData, isAvailable: e.target.checked })}
                            />
                            Available
                        </label>
                    </div>

                    {/* Variants Section */}
                    <div style={{ ...styles.formGroup, borderTop: isDarkMode ? '1px solid #444' : '1px solid #eee', paddingTop: '15px' }}>
                        <label style={{ ...styles.label, marginBottom: '10px', display: 'flex', justifyContent: 'space-between' }}>
                            Variants
                            <button
                                type="button"
                                onClick={() => {
                                    setFormData(prev => ({
                                        ...prev,
                                        variants: [...(prev.variants || []), { name: '', price: '', isAvailable: true }]
                                    }));
                                }}
                                style={styles.actionBtnSmall}
                            >+ Add Variant</button>
                        </label>

                        {(formData.variants || []).map((variant, index) => (
                            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                                <input
                                    placeholder="Name (e.g. Small)"
                                    value={variant.name}
                                    onChange={e => {
                                        const newVariants = [...formData.variants];
                                        newVariants[index].name = e.target.value;
                                        setFormData({ ...formData, variants: newVariants });
                                    }}
                                    style={{ ...styles.input, flex: 2 }}
                                />
                                <input
                                    type="number"
                                    placeholder="Price"
                                    value={variant.price}
                                    onChange={e => {
                                        const newVariants = [...formData.variants];
                                        newVariants[index].price = e.target.value;
                                        setFormData({ ...formData, variants: newVariants });
                                    }}
                                    style={{ ...styles.input, flex: 1 }}
                                />
                                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={variant.isAvailable !== false}
                                        onChange={e => {
                                            const newVariants = [...formData.variants];
                                            newVariants[index].isAvailable = e.target.checked;
                                            setFormData({ ...formData, variants: newVariants });
                                        }}
                                        style={{ marginRight: '4px' }}
                                    />
                                    <span style={{ fontSize: '0.8rem' }}>Avail.</span>
                                </label>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const newVariants = formData.variants.filter((_, i) => i !== index);
                                        setFormData({ ...formData, variants: newVariants });
                                    }}
                                    style={{ ...styles.actionBtnSmall, color: '#e74c3c' }}
                                    title="Remove Variant"
                                >
                                    &times;
                                </button>
                            </div>
                        ))}
                        {(!formData.variants || formData.variants.length === 0) && (
                            <p style={{ fontSize: '0.8rem', color: isDarkMode ? '#888' : '#aaa', fontStyle: 'italic' }}>No variants added.</p>
                        )}
                    </div>

                    <div style={styles.footer}>
                        {initialData && (
                            <button type="button" onClick={handleDelete} style={styles.deleteBtn}>Delete Item</button>
                        )}
                        <div style={{ flex: 1 }}></div>
                        <button type="button" onClick={onClose} style={styles.cancelBtn}>Cancel</button>
                        <button type="submit" style={styles.saveBtn}>
                            {initialData ? 'Update Item' : 'Create Item'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const getStyles = (isDark) => ({
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    },
    modal: {
        backgroundColor: isDark ? '#2D3436' : '#fff',
        padding: '24px',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflowY: 'auto',
        color: isDark ? '#fff' : '#333',
        boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        borderBottom: isDark ? '1px solid #444' : '1px solid #eee',
        paddingBottom: '12px'
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        color: isDark ? '#aaa' : '#666'
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px'
    },
    imageSection: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '10px'
    },
    previewContainer: {
        width: '100%',
        height: '160px',
        borderRadius: '8px',
        overflow: 'hidden',
        backgroundColor: isDark ? '#444' : '#f0f0f0',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        border: isDark ? '1px dashed #666' : '1px dashed #ccc'
    },
    imagePreview: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    placeholderImage: {
        color: isDark ? '#aaa' : '#888',
        fontSize: '0.9rem'
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1
    },
    label: {
        marginBottom: '6px',
        fontSize: '0.9rem',
        fontWeight: '500',
        color: isDark ? '#ccc' : '#555'
    },
    input: {
        padding: '10px 12px',
        borderRadius: '6px',
        border: isDark ? '1px solid #555' : '1px solid #ddd',
        backgroundColor: isDark ? '#333' : '#fff',
        color: isDark ? '#fff' : '#333',
        fontSize: '0.95rem',
        outline: 'none'
    },
    row: {
        display: 'flex',
        gap: '16px',
        alignItems: 'center'
    },
    checkboxLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '0.9rem',
        cursor: 'pointer',
        color: isDark ? '#eee' : '#333'
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '20px',
        paddingTop: '16px',
        borderTop: isDark ? '1px solid #444' : '1px solid #eee'
    },
    cancelBtn: {
        padding: '10px 20px',
        background: 'transparent',
        border: isDark ? '1px solid #555' : '1px solid #ccc',
        borderRadius: '6px',
        color: isDark ? '#ccc' : '#666',
        cursor: 'pointer',
        fontWeight: '500'
    },
    saveBtn: {
        padding: '10px 20px',
        backgroundColor: '#0F9D58',
        border: 'none',
        borderRadius: '6px',
        color: 'white',
        cursor: 'pointer',
        fontWeight: '600'
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isDark ? 'rgba(45, 52, 54, 0.9)' : 'rgba(255, 255, 255, 0.9)',
        zIndex: 10,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '12px'
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #0F9D58',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    cropperContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        backgroundColor: '#333',
        borderRadius: '8px',
        padding: '10px',
        overflow: 'hidden'
    },
    cropperWrapper: {
        position: 'relative',
        width: '100%',
        height: '300px',
        backgroundColor: '#000'
    },
    controls: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginTop: '10px',
        gap: '10px'
    },
    controlRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        color: 'white'
    },
    zoomRange: {
        flex: 1,
        cursor: 'pointer'
    },
    cropActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        marginTop: '5px'
    },
    deleteBtn: {
        padding: '10px 20px',
        backgroundColor: '#D32F2F',
        border: 'none',
        borderRadius: '6px',
        color: 'white',
        cursor: 'pointer',
        fontWeight: '600',
        marginRight: 'auto'
    }
});
